import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { AlertCircle, CheckCircle, Mail, ArrowLeft, Lock } from 'lucide-react';
import { supabase } from '../../lib/supabase';

const ForgotPassword = () => {
  const [searchParams] = useSearchParams();
  const returnTo = searchParams?.get('from') || 'admin-login';

  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const validateEmail = (val) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(val);

  const getBackLink = () => {
    if (returnTo === 'driver') return '/driver-login';
    if (returnTo === 'admin') return '/admin-login';
    return '/admin-login';
  };

  const getBackLabel = () => {
    if (returnTo === 'driver') return 'Retour à la connexion chauffeur';
    return 'Retour à la connexion admin';
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setError('');

    if (!email?.trim()) {
      setError('Veuillez saisir votre adresse email.');
      return;
    }
    if (!validateEmail(email?.trim())) {
      setError('Adresse email invalide. Veuillez vérifier le format.');
      return;
    }

    setLoading(true);
    try {
      const { error: resetError } = await supabase?.auth?.resetPasswordForEmail(email?.trim()?.toLowerCase(), {
        redirectTo: 'https://adtexpress.cloud/update-password',
      });

      if (resetError) {
        throw resetError;
      }

      setSuccess(true);
    } catch (err) {
      console.error('[FORGOT-PASSWORD] Error:', err);
      // Always show success for security (don't reveal if email exists)
      setSuccess(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mb-4">
            <div className="w-12 h-12 bg-green-600 rounded-lg flex items-center justify-center mr-3">
              <span className="text-white font-bold text-lg">ADT</span>
            </div>
            <span className="text-xl font-bold text-gray-900">ADT Express</span>
          </div>
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Lock size={28} className="text-green-600" />
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
            Mot de passe oublié ?
          </h1>
          <p className="text-sm sm:text-base text-gray-600">
            Entrez votre adresse email pour recevoir un lien de réinitialisation.
          </p>
        </div>

        {/* Card */}
        <div className="bg-white rounded-xl shadow-md p-6 sm:p-8">
          {success ? (
            <div className="space-y-6">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-start space-x-3">
                <CheckCircle size={20} className="text-green-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-green-800 mb-1">Email envoyé</p>
                  <p className="text-sm text-green-700">
                    Si ce compte existe, un email de réinitialisation a été envoyé.
                  </p>
                  <p className="text-xs text-green-600 mt-2">
                    Vérifiez également votre dossier spam. Le lien vous redirigera vers la page de création de mot de passe.
                  </p>
                </div>
              </div>

              <div className="space-y-3">
                <button
                  onClick={() => { setSuccess(false); setEmail(''); }}
                  className="w-full px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium"
                >
                  Renvoyer le lien
                </button>
                <Link
                  to={getBackLink()}
                  className="block w-full text-center px-4 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium"
                >
                  Retour à la connexion
                </Link>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start space-x-3">
                  <AlertCircle size={18} className="text-red-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Adresse email
                </label>
                <div className="relative">
                  <Mail size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e?.target?.value)}
                    placeholder="votre@email.com"
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-gray-900 placeholder:text-gray-400 text-sm"
                    disabled={loading}
                    required
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full flex items-center justify-center space-x-2 px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 active:bg-green-800 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors font-medium text-sm min-h-[44px]"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    <span>Envoi en cours...</span>
                  </>
                ) : (
                  <span>Envoyer le lien de réinitialisation</span>
                )}
              </button>
            </form>
          )}
        </div>

        {/* Back link */}
        <div className="mt-6 text-center">
          <Link
            to={getBackLink()}
            className="inline-flex items-center space-x-2 text-sm text-gray-600 hover:text-gray-900 font-medium min-h-[44px] py-2"
          >
            <ArrowLeft size={16} />
            <span>{getBackLabel()}</span>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
